<template>
  <div
    id="map-container"
    class="mapboxgl-map relative"
    :class="{
      'w-full': isDesktop,
      'h-52 w-full md:h-60': !isDesktop,
    }"
  >
    <div
      v-if="isDesktop && destinationLoads?.length > 0"
      class="absolute bottom-0 left-0 z-10 flex w-full flex-col items-start gap-5 bg-[linear-gradient(180deg,rgba(41,48,61,0)_0%,rgba(41,48,61,0.5)_50%)] p-6 pr-0 pt-10 font-['Poppins']"
    >
      <p class="text-lg font-medium">Loads at your destination</p>
      <div class="flex h-[255px] gap-6 overflow-x-scroll">
        <LoadDetail
          v-for="(recommendation, index) in destinationLoads"
          :key="index"
          :load="recommendation"
          :open-in-new-tab="true"
          class="mt-auto max-h-[250px] min-w-[300px]"
        ></LoadDetail>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';
import {
  watch,
  computed,
  ref,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
} from 'vue';
import { useToast } from 'vue-toast-notification';
import { useRouter } from 'vue-router';
import mapboxgl from 'mapbox-gl';
import '../../../node_modules/mapbox-gl/dist/mapbox-gl.css';
import * as turf from '@turf/turf';
import { LoadDetail } from '@/components';
import { useScreenSize } from '@/composables';
import {
  assignedVehicleLabelIcon,
  assignedVehicleLabel,
  assignedVehicleIcon,
  hqIcon,
  hqLabel,
  destinationLabel,
  destinationIcon,
  originLabel,
  originIcon,
  pingsClusterIcon,
  pingIcon,
  stopsClusterIcon,
  unassignedVehicleIcon,
  unverifiedVehicleIcon,
} from '@/assets/map-icons';

const props = defineProps({
  load: Object,
  isDesktop: Boolean,
});

const store = useStore();
const router = useRouter();
const toast = useToast();
const { screenSize } = useScreenSize();

const isMobile = computed(() => screenSize.value === 'mobile');

const isLoadBoard = computed(() =>
  router.currentRoute.value?.path?.includes('load-board')
);

const load = props.load;

const origin = computed(() => load?.stops?.[0]);

const destination = computed(() => load.stops?.[load.stops.length - 1]);

const assignedVehicle = computed(() => store.state.loads.assigned_vehicle);

const assignedCarrierVehicleAreas = computed(
  () => store.state.loads.assigned_vehicle.carrier_vehicle_areas || []
);

const nearbyVehicles = computed(() => store.state.loads.near_by_vehicles);

const nearbyVehiclesList = computed(() =>
  nearbyVehicles.value
    .map(({ carrier_vehicle, vehicle, carrier_vehicle_areas }) => ({
      id: carrier_vehicle?.id,
      identifier:
        carrier_vehicle?.truck_number ||
        carrier_vehicle?.external_id ||
        carrier_vehicle?.id,
      vin: vehicle?.vin,
      isUnverified: carrier_vehicle?.is_uninsured_eld,
      distanceToOrigin:
        carrier_vehicle_areas[0]?.distance_in_miles || 'unknown',
      isNearby: true,
    }))
    .sort((a, b) => a.distanceToOrigin - b.distanceToOrigin)
);

const assignedNearbyVehicles = computed(() => {
  return nearbyVehicles.value.filter(
    (nearbyVehicle) =>
      nearbyVehicle.carrier_vehicle?.id !==
        assignedVehicle.value.carrier_vehicle?.id &&
      nearbyVehicle.carrier_vehicle?.id !==
        alertVehicle.value?.carrier_vehicle_area?.carrier_vehicle?.id
  );
});

const alertVehicle = computed(
  () => store.state.loads.alert_vehicles[0]?.carrier_vehicle_area || {}
);

const carrierVehiclesDropdownSelected = ref({});

const closeCarrierVehiclesDropdown = ref(false);

const handleClickAway = () => {
  closeCarrierVehiclesDropdown.value = !closeCarrierVehiclesDropdown.value;
};

onMounted(() => {
  window.addEventListener('click', handleClickAway);

  const load_id = props?.load?.id || '';

  store.dispatch('loads/getRecommendedLoads', {
    type: 'loads_at_destination',
    load_id,
  });
});

const destinationLoads = computed(
  () => store.state.loads?.loads_at_destination?.loads
);

onUnmounted(() => window.removeEventListener('click', handleClickAway));

const hasMap = ref(false);

const showDropdown = ref(false);

// get user permissions
const canTrackAndTrace = computed(
  () => store.state.users?.currentUser.paywall?.can_track_and_trace
);

const activePageTab = ref(router.currentRoute.value.query.tab || 'details');

let mapbox = null;

const isMapExpanded = ref(false);

// fetch load data
const fetchLoadData = () => {
  if (!isLoadBoard?.value) {
    store.dispatch('loads/getUserTicketRole');
  }
};

fetchLoadData();

const loadRoute = computed(() => store.state.loads.load_route);

const destinationRoute = computed(() => store.state.loads.destination_route);

const originCoordinates = computed(() =>
  origin.value.latitude_for_map && origin.value.longitude_for_map
    ? [origin.value.longitude_for_map, origin.value.latitude_for_map]
    : []
);

const vehicleLocationCoordinates = computed(() =>
  assignedCarrierVehicleAreas.value?.length
    ? [
        assignedCarrierVehicleAreas.value[0].lon,
        assignedCarrierVehicleAreas.value[0].lat,
      ]
    : []
);

const wasLoadCreatedAfterStopAppt = (stop) => {
  if (stop.scheduled_window_ended_at_in_stop_timezone) {
    return (
      Date.parse(stop.created_at) >
      Date.parse(stop.scheduled_window_ended_at_in_stop_timezone)
    );
  }
  return false;
};

const formatRouteCoordinates = (stops, type = 'vehicle') => {
  let coordinates = '';
  let futureStops = stops;
  if (type === 'vehicle') {
    coordinates += `${vehicleLocationCoordinates.value[0]},${vehicleLocationCoordinates.value[1]};`;
    futureStops = stops.filter(
      (stop) =>
        !stop.arrived_at_in_stop_timezone &&
        !stop.departed_at_in_stop_timezone &&
        !wasLoadCreatedAfterStopAppt(stop)
    );
  }
  futureStops.forEach((stop, index) => {
    if (stop.latitude_for_map && stop.longitude_for_map) {
      coordinates += `${stop.longitude_for_map},${stop.latitude_for_map}${
        index !== futureStops.length - 1 ? ';' : ''
      }`;
    }
  });
  return coordinates;
};

const addIcons = async () => {
  const icons = {
    'unverified-vehicle': unverifiedVehicleIcon,
    'hq-label': hqLabel,
    hq: hqIcon,
    'assigned-vehicle-label': assignedVehicleLabel,
    'assigned-vehicle': assignedVehicleIcon,
    'destination-label': destinationLabel,
    destination: destinationIcon,
    'origin-label': originLabel,
    origin: originIcon,
    ping: pingIcon,
    'unassigned-vehicle': unassignedVehicleIcon,
    'stops-cluster': stopsClusterIcon,
    'pings-cluster': pingsClusterIcon,
    'assigned-vehicle-label-bg': assignedVehicleLabelIcon,
  };

  try {
    await Promise.all(
      Object.keys(icons).map(
        (key) =>
          new Promise((resolve, reject) => {
            mapbox.loadImage(icons[key], function (error, res) {
              if (error) reject(error);
              if (!mapbox.hasImage(key)) {
                mapbox.addImage(key, res);
              }
              resolve();
            });
          })
      )
    );
    return true;
  } catch (error) {
    console.log('error loading icons', error);
    return false;
  }
};

const removeScale = () => {
  // possibly only do this on mobile for load board only
  if (document.querySelector('.mapboxgl-ctrl-scale')) {
    document.querySelector('.mapboxgl-ctrl-scale').remove();
  }
};

let mapMoveEventsCount = 0;
let firstSymbolId;

const addMap = () => {
  if (mapbox) {
    mapbox.remove();
  }
  const token = process.env.MAPBOX_TOKEN;

  if (!token) {
    // set value here to say no tkn
    return;
  }
  mapboxgl.accessToken = token;

  let zoom = 5;
  let minZoom = 3;
  if (!isMobile?.value) {
    if (load?.posted_miles < 1400 && load?.posted_miles > 900) {
      zoom = 6;
      minZoom = 3.5;
    } else if (load?.posted_miles < 1000) {
      zoom = 7;
      minZoom = 3.5;
    }
  }

  mapbox = new mapboxgl.Map({
    container: 'map-container', // container ID
    style: import.meta.env.VITE_MAPBOX_STYLE_TEMPLATE, // style URL
    center: [-98.5795, 39.8283], // starting position [lng, lat], center of US
    // center: centerPoint.value || originCoordinates.value || [-98.5795, 39.8283],
    zoom,
    minZoom,
    logoPosition: 'bottom-right',
    maxBounds: [
      [-163.148014, 11.267979], // Southwest coordinates
      [-53.447696, 70.63585], // Northeast coordinates
    ],
  });
  mapbox.on('load', async () => {
    const layers = mapbox.getStyle().layers;
    // Find the index of the first symbol layer in the map style.
    for (const layer of layers) {
      if (layer.type === 'symbol') {
        firstSymbolId = layer.id;
        break;
      }
    }
    // mapbox.resize();
    await addIcons();
    hasMap.value = true;
  });
  mapbox.on('moveend', () => {
    mapMoveEventsCount++;
    if (mapMoveEventsCount === 2) {
      document
        .querySelector('.reset-view-button')
        .classList.remove('invisible');
    }
  });
  const scale = new mapboxgl.ScaleControl({
    maxWidth: 250,
    unit: 'imperial',
  });
  mapbox.addControl(scale);
  mapbox.addControl(new mapboxgl.FullscreenControl());
  const nav = new mapboxgl.NavigationControl({ showCompass: false }); // set showCompass to true if you also want to display the compass
  mapbox.addControl(nav, 'top-right');

  removeScale();
};

onMounted(() => {
  addMap();

  setCarrierVehiclesDropdown();
  fetchRoutes();
});

const fetchRoutes = async () => {
  if (load?.id) {
    toast.open({
      message: 'Loading route...',
      position: 'top-right',
      duration: 2000,
    });
  }

  if (
    load.stops?.length &&
    !destination.value.arrived_at_in_stop_timezone &&
    vehicleLocationCoordinates.value.length
  ) {
    await store.dispatch('loads/getRoute', {
      type: 'destination',
      coordinates: formatRouteCoordinates(load.stops),
    });
  } else if (load.stops?.length && !assignedVehicle.value.carrier_vehicle?.id) {
    await store.dispatch('loads/getRoute', {
      type: 'load',
      coordinates: formatRouteCoordinates(load.stops, 'load'),
    });
  }
  hasFetchedRoutes.value = true;
};

const hasLayers = ref(false);

const zoomToLoadRoute = () => {
  addLoadStopsToBounds();
  addAssignedVehicleLocationToBounds();
  addNearbyVehicleLocationsToBounds();
  addAlertVehicleLocationToBounds();
  document.querySelector('.reset-view-button').classList.add('invisible');
  mapMoveEventsCount = 0;
  extendBounds();
};

const addLayers = () => {
  coordinates = {};
  toggleButtons = new ToggleControl();
  mapbox.addControl(toggleButtons, 'top-left');
  addLoadStopsGeofencesLines();
  addLoadStopsGeofences();
  addNoAssignedVehicleRoute();
  createAssignedVehicleLocation();
  addAssignedVehicleProjectedRoute();
  addAlertVehicleLine();
  addCarrierHq();
  addAlertVehicleLocation();
  addAssignedVehicleLocation();
  addLoadStops();
  addLoadStopsToBounds();
  addAssignedVehicleLocationToBounds();
  addNearbyVehicleLocationsToBounds();
  addAlertVehicleLocationToBounds();
  mapbox.once('idle', () => {
    extendBounds();
  });
  hasLayers.value = true;
};

const eldMarkerSize = 75;

// create pulsing dot marker

const pulsingDot = {
  size: eldMarkerSize,
  width: eldMarkerSize,
  height: eldMarkerSize,
  data: new Uint8Array(eldMarkerSize * eldMarkerSize * 4),
  isCarrierVehicleOnRoute: false,
  stopPulse: false,

  // When the layer is added to the map,
  // get the rendering context for the map canvas.
  onAdd: function () {
    const canvas = document.createElement('canvas');
    canvas.width = this.width;
    canvas.height = this.height;
    this.context = canvas.getContext('2d', { willReadFrequently: true });
  },

  // Call once before every frame where the icon will be used.
  render: function () {
    const duration = 2000;
    const t = (performance.now() % duration) / duration;

    const radius = (this.size / 2) * 0.3;
    const outerRadius = (this.size / 2) * 0.7 * t + radius;
    const context = this.context;

    // Draw the outer circle.
    context.clearRect(0, 0, this.width, this.height);
    context.beginPath();
    context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
    context.fillStyle = this.stopPulse
      ? `rgba(219, 55, 19, 0)`
      : this.isCarrierVehicleOnRoute
        ? `rgba(84, 199, 116, ${0.6 - t})`
        : `rgba(219, 55, 19, ${0.6 - t})`;
    // context.fillStyle = this.isCarrierVehicleOnRoute
    //   ? `rgba(219, 55, 19, 1)`
    //   : `rgba(219, 55, 19, ${0.6 - t})`;
    context.fill();

    // Draw the inner circle.
    context.beginPath();
    context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
    context.fillStyle = this.isCarrierVehicleOnRoute
      ? 'rgba(84, 199, 116, 1)'
      : 'rgba(219, 55, 19, 1)';
    context.strokeStyle = this.isCarrierVehicleOnRoute
      ? 'rgba(84, 199, 116, 1)'
      : 'rgba(237, 240, 240, 0.7)';
    context.lineWidth = 2;
    context.fill();
    context.stroke();

    // Update this image's data with data from the canvas.
    this.data = context.getImageData(0, 0, this.width, this.height).data;

    // Continuously repaint the map, resulting
    // in the smooth animation of the dot.
    mapbox.triggerRepaint();
    // Return `true` to let the map know that the image was updated.
    return true;
  },
};

const greyPulsingDot = {
  size: eldMarkerSize,
  width: eldMarkerSize,
  height: eldMarkerSize,
  data: new Uint8Array(eldMarkerSize * eldMarkerSize * 4),

  // When the layer is added to the map,
  // get the rendering context for the map canvas.
  onAdd: function () {
    const canvas = document.createElement('canvas');
    canvas.width = this.width;
    canvas.height = this.height;
    this.context = canvas.getContext('2d', { willReadFrequently: true });
  },

  // Call once before every frame where the icon will be used.
  render: function () {
    const duration = 2000;
    const t = (performance.now() % duration) / duration;

    const radius = (this.size / 2) * 0.3;
    const outerRadius = (this.size / 2) * 0.7 * t + radius;
    const context = this.context;

    // Draw the outer circle.
    context.clearRect(0, 0, this.width, this.height);
    context.beginPath();
    context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
    context.fillStyle = `rgba(55, 64, 83, ${0.6 - t})`;
    context.fill();

    // Draw the inner circle.
    context.beginPath();
    context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
    context.fillStyle = 'rgba(55, 64, 83, 1)';
    context.strokeStyle = 'rgba(237, 240, 240, 0.7)';
    context.lineWidth = 2;
    context.fill();
    context.stroke();

    // Update this image's data with data from the canvas.
    this.data = context.getImageData(0, 0, this.width, this.height).data;

    // Continuously repaint the map, resulting
    // in the smooth animation of the dot.
    mapbox.triggerRepaint();
    // Return `true` to let the map know that the image was updated.
    return true;
  },
};

const bluePulsingDotSize = 75;

const bluePulsingDot = {
  size: bluePulsingDotSize,
  width: bluePulsingDotSize,
  height: bluePulsingDotSize,
  data: new Uint8Array(bluePulsingDotSize * bluePulsingDotSize * 4),
  isCarrierVehicleOnRoute: false,
  stopPulse: false,

  // When the layer is added to the map,
  // get the rendering context for the map canvas.
  onAdd: function () {
    const canvas = document.createElement('canvas');
    // canvas.style.cursor = 'pointer';
    canvas.width = this.width;
    canvas.height = this.height;
    this.context = canvas.getContext('2d', { willReadFrequently: true });
  },

  // Call once before every frame where the icon will be used.
  render: function () {
    const duration = 2000;
    const t = (performance.now() % duration) / duration;

    const radius = (this.size / 2) * 0.35;
    const outerRadius = (this.size / 2) * 1 * t + radius;
    const context = this.context;

    // Draw the outer circle.
    context.clearRect(0, 0, this.width, this.height);
    context.fill();

    context.beginPath();
    context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2);
    context.fillStyle = `rgba(67, 135, 216, ${0.6 - t})`;
    context.fill();

    // Draw the inner circle.
    context.beginPath();
    context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 10);
    context.fillStyle = '#4285D7';
    context.strokeStyle = '#4285D7';
    context.lineWidth = 5;
    context.fill();
    context.stroke();

    // Update this image's data with data from the canvas.
    this.data = context.getImageData(0, 0, this.width, this.height).data;

    // Continuously repaint the map, resulting
    // in the smooth animation of the dot.
    mapbox.triggerRepaint();
    // Return `true` to let the map know that the image was updated.
    return true;
  },
};

let nearestTruckLine = {
  layerId: null,
  layerLabelId: null,
};

const hoveredEldColor = '#a42a0e'; // '#2563eb';
const line_width_outer = 6;
const line_width_inner = 4;

const addAlertVehicleLine = () => {
  const { carrier_vehicle_areas = [] } = alertVehicle.value;

  const sourceId = `source-alert-vehicle-origin-line`;
  const layerId = `layer-alert-vehicle-origin-line`;
  const layerLabelId = `layer-alert-vehicle-origin-line-label`;

  if (mapbox.getLayer(layerId)) {
    mapbox.removeLayer(layerId);
  }
  if (mapbox.getLayer(layerLabelId)) {
    mapbox.removeLayer(layerLabelId);
  }
  if (mapbox.getSource(sourceId)) {
    mapbox.removeSource(sourceId);
  }

  if (
    carrier_vehicle_areas.length &&
    originCoordinates.value.length &&
    !assignedNearbyVehicles.value.length &&
    !assignedVehicle.value.carrier_vehicle?.id
  ) {
    const is_nearest_truck = true;
    const distance = Number(outOfRangeAlertDistance.value);
    const distanceText = `${distance.toFixed(0)} mi`;
    const connectingLineFeature = turf.lineString([
      [carrier_vehicle_areas[0].lon, carrier_vehicle_areas[0].lat],
      originCoordinates.value,
    ]);

    if (!mapbox.getSource(sourceId)) {
      mapbox.addSource(sourceId, {
        type: 'geojson',
        data: connectingLineFeature,
      });
    }

    if (!mapbox.getLayer(layerId)) {
      mapbox.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          visibility: is_nearest_truck ? 'visible' : 'none',
        },
        paint: {
          'line-color': is_nearest_truck ? '#db3713' : hoveredEldColor,
          'line-width': 2,
          'line-dasharray': [2, 2],
        },
      });
    }

    if (!mapbox.getLayer(layerLabelId)) {
      mapbox.addLayer({
        id: layerLabelId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': distanceText,
          'text-anchor': 'top', // options are [center, left, right, top, bottom, top-left, top-right, bottom-left, bottom-right]
          // 'text-justify': 'start',
          'symbol-placement': 'line-center', // places the text in the center of the line
          'symbol-spacing': 5000,
          'text-size': 14,
          'text-allow-overlap': false,
          'text-ignore-placement': false,
          'symbol-avoid-edges': true,
          visibility: is_nearest_truck ? 'visible' : 'none',
        },
        paint: {
          'text-color': is_nearest_truck ? '#db3713' : hoveredEldColor,
          'text-halo-color': '#edf0f0',
          'text-halo-width': 1,
        },
      });
    }

    if (is_nearest_truck) {
      nearestTruckLine.layerId = layerId;
      nearestTruckLine.layerLabelId = layerLabelId;
    }
  }
};

const addAlertVehicleLocation = () => {
  const {
    carrier_vehicle = {},
    vehicle = {},
    carrier_vehicle_areas = [],
  } = alertVehicle.value;

  const sourceId = `source-alert-vehicle-location`;
  const layerId = `layer-alert-vehicle-location`;
  const unverifiedPulsingDotLayerId = layerId + '-pulsing-dot';

  if (mapbox.getLayer(unverifiedPulsingDotLayerId)) {
    mapbox.removeLayer(unverifiedPulsingDotLayerId);
  }
  if (mapbox.getLayer(layerId)) {
    mapbox.removeLayer(layerId);
  }
  if (mapbox.getSource(sourceId)) {
    mapbox.removeSource(sourceId);
  }

  if (
    carrier_vehicle_areas?.length &&
    !assignedNearbyVehicles.value.length &&
    !assignedVehicle.value.carrier_vehicle?.id
  ) {
    const point = turf.point(
      [carrier_vehicle_areas[0].lon, carrier_vehicle_areas[0].lat],
      {
        carrier_vehicle: carrier_vehicle,
        vehicle: vehicle,
        area: carrier_vehicle_areas[0],
        isUnverified: carrier_vehicle.is_uninsured_eld,
        image: carrier_vehicle.is_uninsured_eld
          ? 'unverified-vehicle'
          : 'pulsing-dot',
      }
    );
    mapbox.addSource(sourceId, {
      type: 'geojson',
      data: point,
    });

    if (!mapbox?.hasImage('pulsing-dot')) {
      mapbox.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 });
    }

    if (!mapbox?.hasImage('grey-pulsing-dot')) {
      mapbox.addImage('grey-pulsing-dot', greyPulsingDot, {
        pixelRatio: 2,
      });
    }

    mapbox.addLayer({
      id: unverifiedPulsingDotLayerId,
      type: 'symbol',
      source: sourceId,
      filter: ['==', ['get', 'isUnverified'], true],
      layout: {
        'icon-image': 'grey-pulsing-dot',
        visibility: 'visible',
        'icon-allow-overlap': true,
      },
    });

    mapbox.addLayer({
      id: layerId,
      type: 'symbol',
      source: sourceId,
      layout: {
        'icon-image': ['get', 'image'],
        visibility: 'visible',
        'icon-allow-overlap': true,
      },
    });

    let hoverLayerId;
    let hoverLabelId;
    let hoverSourceId;

    // add hover effect
    mapbox.on('mouseenter', layerId, (e) => {
      const featureClicked = e.features?.[0];
      if (featureClicked) {
        const area = featureClicked.properties.area
          ? JSON.parse(featureClicked.properties.area)
          : {};
        const carrier_vehicle = featureClicked.properties.carrier_vehicle
          ? JSON.parse(featureClicked.properties.carrier_vehicle)
          : {};
        hoverSourceId = `hover-source-alert-vehicle-location`;
        const point = turf.point([area.lon, area.lat]);
        // add point source for hover effect
        if (!mapbox.getSource(hoverSourceId)) {
          mapbox.addSource(hoverSourceId, {
            type: 'geojson',
            data: point,
          });
        }

        hoverLayerId = `hover-layer-alert-vehicle`;
        mapbox.getCanvas().style.cursor = 'pointer';
        if (!mapbox.getLayer(hoverLayerId)) {
          mapbox.addLayer({
            id: hoverLayerId,
            type: 'circle',
            source: hoverSourceId,
            paint: {
              'circle-radius': carrier_vehicle.is_uninsured_eld ? 10 : 8,
              'circle-color': 'white',
              'circle-stroke-color': carrier_vehicle.is_uninsured_eld
                ? '#374053'
                : '#DD3813',
              'circle-stroke-width': 2,
              'circle-opacity': 0,
            },
          });
        }
        hoverLabelId = `hover-label-${carrier_vehicle.is_uninsured_eld ? 'unverified' : 'alert'}-vehicle`;
        if (carrier_vehicle.is_uninsured_eld) {
          mapbox.addLayer({
            id: hoverLabelId,
            type: 'symbol',
            source: hoverSourceId,
            layout: {
              'text-variable-anchor': ['left', 'bottom', 'top', 'right'],
              'text-radial-offset': 1.5,
              'text-field': `${carrier_vehicle.is_uninsured_eld ? 'Unverified' : ''} Vehicle Out Of Range`,
              'text-justify': 'left',
              'icon-text-fit': 'both',
              'icon-text-fit-padding': [5, 5, 5, 5],
              'icon-image': carrier_vehicle.is_uninsured_eld
                ? 'hq-label'
                : 'nearby-label',
              'text-size': 12,
              'text-letter-spacing': 0.02,
              'text-line-height': 1.2,
              // 'icon-allow-overlap': true,
              // 'icon-ignore-placement': true,
            },
            paint: {
              'text-color': '#ffffff',
            },
          });
        }
      }
    });

    // remove hover effect
    mapbox.on('mouseleave', layerId, () => {
      mapbox.getCanvas().style.cursor = '';
      if (mapbox.getLayer(hoverLabelId)) {
        mapbox.removeLayer(hoverLabelId);
      }
      if (mapbox.getLayer(hoverLayerId)) {
        mapbox.removeLayer(hoverLayerId);
      }
      if (mapbox.getSource(hoverSourceId)) {
        mapbox.removeSource(hoverSourceId);
      }
    });
  }
};

let assignedVehicleLocation;

const createAssignedVehicleLocation = () => {
  const { carrier_vehicle = {}, vehicle = {} } = assignedVehicle.value;
  if (assignedCarrierVehicleAreas.value.length) {
    assignedVehicleLocation = turf.point(
      [
        assignedCarrierVehicleAreas.value[0].lon,
        assignedCarrierVehicleAreas.value[0].lat,
      ],
      {
        carrier_vehicle: carrier_vehicle,
        vehicle: vehicle,
        area: assignedCarrierVehicleAreas.value[0],
        isUnverified: carrier_vehicle.is_uninsured_eld,
        image: carrier_vehicle.is_uninsured_eld
          ? 'unverified-vehicle'
          : 'pulsing-dot',
      }
    );
  }
};

const addAssignedVehicleProjectedRoute = () => {
  const { carrier_vehicle_areas = [] } = assignedVehicle.value;
  const source = `destination-route`;
  const line = `destination-route-line`;
  const border = `destination-route-line-border`;

  if (mapbox.getLayer(line)) {
    mapbox.removeLayer(line);
  }
  if (mapbox.getLayer(border)) {
    mapbox.removeLayer(border);
  }
  if (mapbox.getSource(source)) {
    mapbox.removeSource(source);
  }
  if (
    carrier_vehicle_areas.length &&
    destinationRoute.value &&
    load.status !== 'delivered'
  ) {
    mapbox.addSource(source, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: destinationRoute.value,
      },
    });

    mapbox.addLayer({
      id: line,
      type: 'line',
      source: source,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
        visibility: 'visible',
      },
      paint: {
        'line-color': '#374053',
        'line-width': line_width_outer,
      },
    });

    mapbox.addLayer({
      id: border,
      type: 'line',
      source: source,
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
        visibility: 'visible',
      },
      paint: {
        'line-color': '#D5E4F6',
        'line-width': line_width_inner,
      },
    });
  }
};

const addAssignedVehicleLocation = () => {
  const { carrier_vehicle_areas = [] } = assignedVehicle.value;
  const sourceId = `vehicle-history`;
  //last ping
  const lastPing = 'last-ping';
  const lastPingDot = 'last-ping-dot';
  const lastPingLabel = 'last-ping-label';

  if (mapbox.getLayer(lastPing)) {
    mapbox.removeLayer(lastPing);
  }
  if (mapbox.getLayer(lastPingDot)) {
    mapbox.removeLayer(lastPingDot);
  }
  if (mapbox.getLayer(lastPingLabel)) {
    mapbox.removeLayer(lastPingLabel);
  }
  if (mapbox.getSource(sourceId)) {
    mapbox.removeSource(sourceId);
  }
  if (carrier_vehicle_areas.length && assignedVehicleLocation) {
    mapbox.addSource(sourceId, {
      type: 'geojson',
      data: assignedVehicleLocation,
    });

    const dot = 'assigned-vehicle-dot';

    if (!mapbox.hasImage(dot)) {
      mapbox.addImage(dot, bluePulsingDot, {
        pixelRatio: 2,
      });
    }

    mapbox.addLayer({
      id: lastPingDot,
      type: 'symbol',
      source: sourceId,
      layout: {
        'icon-image': dot,
        visibility: 'visible',
        'icon-allow-overlap': true,
      },
    });

    mapbox.addLayer({
      id: lastPing,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-color': '#4285D7',
        'circle-radius': 5,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
      },
      layout: {
        // 'icon-allow-overlap': true,
        visibility: 'visible',
      },
    });
    mapbox.addLayer({
      id: lastPingLabel,
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-variable-anchor': ['left', 'bottom', 'top', 'right'],
        'text-radial-offset': 1.5,
        'text-field': 'Assigned Vehicle',
        'text-justify': 'center',
        'icon-text-fit': 'both',
        'icon-text-fit-padding': [8, 10, 12, 10],
        'icon-image': 'assigned-vehicle-label-bg',
        'text-size': 12,
        'text-letter-spacing': 0.02,
        'text-line-height': 1.2,
      },
      paint: {
        'text-color': '#ffffff',
      },
    });
  }
};

let coordinates = {};

let toggleButtons;

class ToggleControl {
  onAdd(map) {
    this.map = map;
    if (document.querySelector('.toggle-container')) {
      document.querySelector('.toggle-container').remove();
    }
    this.container = document.createElement('div');
    this.container.className =
      'toggle-container flex items-center gap-4 rounded-md p-3 shadow-legend-control mapboxgl-ctrl';
    this.button = document.createElement('button');
    this.button.className =
      'nearby-vehicles pl-1 pr-2 py-1 bg-white rounded-full shadow border justify-start items-center gap-0.5 flex';

    this.svg = `<svg class="hide-vehicles" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M2.99007 4.69718L1.64645 3.35355C1.45118 3.15829 1.45118 2.84171 1.64645 2.64645C1.84171 2.45118 2.15829 2.45118 2.35355 2.64645L13.3536 13.6464C13.5488 13.8417 13.5488 14.1583 13.3536 14.3536C13.1583 14.5488 12.8417 14.5488 12.6464 14.3536L11.3028 13.0099C10.3555 13.6357 9.2203 14 8 14C4.68629 14 2 11.3137 2 8C2 6.7797 2.3643 5.64449 2.99007 4.69718ZM9.08909 10.7962C8.75148 10.9278 8.38417 11 8 11C6.34315 11 5 9.65685 5 8C5 7.61619 5.07189 7.24874 5.20354 6.91065L9.08909 10.7962Z" fill="#5C6B8A"/>
        <path d="M11 8C11 8.22658 10.9749 8.44729 10.9273 8.65951L13.223 10.9552C13.7176 10.0829 14 9.07442 14 8C14 4.68629 11.3137 2 8 2C6.92558 2 5.91712 2.2824 5.0448 2.77703L7.33996 5.07219C7.55132 5.02497 7.77197 5 8 5C9.65685 5 11 6.34315 11 8Z" fill="#5C6B8A"/>
        </svg>`;
    this.svgShowNearbyVehicles = `<svg class="show-vehicles" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="8" cy="8" r="4.5" stroke="#5C6B8A" stroke-width="3"/>
</svg>`;

    this.span = document.createElement('span');
    this.span.textContent = assignedVehicle.value.carrier_vehicle?.id
      ? `Show Nearby Vehicles`
      : `Hide Nearby Vehicles`;
    this.span.className = `text-gray-700 text-xs font-medium leading-none`;
    if (assignedVehicle.value.carrier_vehicle?.id) {
      this.button.innerHTML = this.svgShowNearbyVehicles;
    } else {
      this.button.innerHTML = this.svg;
    }
    this.button.appendChild(this.span);
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const layers = map.getStyle().layers;
      const clusterLayers = layers.filter(
        (layer) => layer.id.includes('cluster') && layer.id.includes('nearby')
      );
      const historyLayers = layers.filter((layer) => {
        return layer.id.includes('vehicle-history');
      });
      if (this.span.textContent === `Hide Nearby Vehicles`) {
        if (document.querySelector('.show-vehicles')) {
          document.querySelector('.show-vehicles').remove();
        }
        if (document.querySelector('.vehicle-history-button')) {
          document.querySelector('.vehicle-history-button').style.display =
            'flex';
          document.querySelector('.vehicle-history-button').innerHTML =
            this.vehicleHistoryButtonShowSvg;
          this.vehicleHistorySpan = document.createElement('span');
          this.historyLength = assignedCarrierVehicleAreas.value.length;
          this.vehicleHistorySpan.textContent =
            this.historyLength > 0
              ? this.historyLength > 1
                ? `Show Vehicle History`
                : `No Vehicle History`
              : `No Vehicle Location`;
          this.vehicleHistorySpan.className = `text-gray-700 text-xs font-medium leading-none`;
          this.vehicleHistoryButton.innerHTML =
            this.vehicleHistoryButtonShowSvg;
          document
            .querySelector('.vehicle-history-button')
            .appendChild(this.vehicleHistorySpan);
        }
        this.button.innerHTML = this.svgShowNearbyVehicles;
        this.span.textContent = `Show Nearby Vehicles`;
        this.button.appendChild(this.span);
        mapbox.setLayoutProperty(
          'layer-nearby-carrier-vehicles',
          'visibility',
          'none'
        );
        if (mapbox.getLayer('layer-nearby-carrier-vehicles-pulsing-dot')) {
          mapbox.setLayoutProperty(
            'layer-nearby-carrier-vehicles-pulsing-dot',
            'visibility',
            'none'
          );
        }
        clusterLayers.forEach((layer) => {
          map.setLayoutProperty(layer.id, 'visibility', 'none');
        });
        delete coordinates['nearby-vehicles'];
        extendBounds();
      } else {
        if (document.querySelector('.show-vehicles')) {
          document.querySelector('.show-vehicles').remove();
        }
        if (document.querySelector('.vehicle-history-button')) {
          document.querySelector('.vehicle-history-button').style.display =
            'none';
        }
        this.button.innerHTML = this.svg;
        this.span.textContent = `Hide Nearby Vehicles`;
        this.button.appendChild(this.span);
        mapbox.setLayoutProperty(
          'layer-nearby-carrier-vehicles',
          'visibility',
          'visible'
        );
        if (mapbox.getLayer('layer-nearby-carrier-vehicles-pulsing-dot')) {
          mapbox.setLayoutProperty(
            'layer-nearby-carrier-vehicles-pulsing-dot',
            'visibility',
            'visible'
          );
        }
        clusterLayers.forEach((layer) => {
          map.setLayoutProperty(layer.id, 'visibility', 'visible');
        });
        historyLayers.forEach((layer) => {
          map.setLayoutProperty(layer.id, 'visibility', 'none');
        });
        const nearbyVehicleCoordinates = [];
        nearbyVehicles.value.forEach(({ carrier_vehicle_areas = [] }) => {
          if (carrier_vehicle_areas.length) {
            nearbyVehicleCoordinates.push([
              carrier_vehicle_areas[0].lon,
              carrier_vehicle_areas[0].lat,
            ]);
          }
        });
        coordinates = {
          ...coordinates,
          'nearby-vehicles': nearbyVehicleCoordinates,
        };
        extendBounds();
      }
    });
    this.vehicleHistoryButton = document.createElement('button');
    this.vehicleHistoryButton.className =
      'vehicle-history-button pl-1 pr-2 py-1 bg-white rounded-full shadow border justify-start items-center gap-0.5 flex';
    this.vehicleHistoryButtonShowSvg = `<svg class="show-history" width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M1.20833 5.10786C2.39725 3.81156 4.10346 3 5.99996 3C7.89645 3 9.60267 3.81156 10.7916 5.10786C10.9782 5.31137 11.2945 5.32504 11.498 5.13839C11.7015 4.95174 11.7152 4.63545 11.5286 4.43194C10.1582 2.93781 8.18823 2 5.99996 2C3.81168 2 1.84171 2.93781 0.47136 4.43194C0.284709 4.63545 0.298376 4.95174 0.501886 5.13839C0.705395 5.32504 1.02168 5.31137 1.20833 5.10786ZM2.91598 6.22291C3.72187 5.4642 4.80615 5 5.99994 5C7.19373 5 8.27801 5.4642 9.0839 6.22291C9.28496 6.4122 9.6014 6.40266 9.79069 6.2016C9.97998 6.00054 9.97043 5.6841 9.76938 5.49481C8.78536 4.56841 7.45849 4 5.99994 4C4.5414 4 3.21452 4.56841 2.23051 5.49481C2.02945 5.6841 2.01991 6.00054 2.2092 6.2016C2.39849 6.40266 2.71492 6.4122 2.91598 6.22291ZM5.9999 7C5.38081 7 4.81541 7.22438 4.37866 7.59688C4.16855 7.77607 3.85296 7.75101 3.67377 7.5409C3.49458 7.3308 3.51964 7.01521 3.72974 6.83602C4.34065 6.31499 5.13413 6 5.9999 6C6.86567 6 7.65915 6.31499 8.27005 6.83602C8.48016 7.01521 8.50522 7.3308 8.32602 7.5409C8.14683 7.75101 7.83124 7.77607 7.62114 7.59688C7.18438 7.22438 6.61898 7 5.9999 7ZM6 10.5C6.55228 10.5 7 10.0523 7 9.5C7 8.94772 6.55228 8.5 6 8.5C5.44772 8.5 5 8.94772 5 9.5C5 10.0523 5.44772 10.5 6 10.5Z" fill="#5C6B8A"/>
</svg>
`;
    this.vehicleHistoryButtonHideSvg = `<svg class="hide-history" width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.99996 2C5.72381 2 5.49996 2.22386 5.49996 2.5C5.49996 2.77614 5.72381 3 5.99996 3C7.89645 3 9.60267 3.81156 10.7916 5.10786C10.9782 5.31137 11.2945 5.32504 11.498 5.13839C11.7015 4.95174 11.7152 4.63545 11.5286 4.43194C10.1582 2.93781 8.18823 2 5.99996 2ZM3.10594 3.67808C3.35312 3.55496 3.45368 3.25477 3.33056 3.0076C3.20743 2.76042 2.90725 2.65986 2.66007 2.78298C1.83144 3.19574 1.09031 3.75708 0.47136 4.43194C0.284709 4.63545 0.298376 4.95174 0.501886 5.13839C0.705395 5.32504 1.02168 5.31137 1.20833 5.10786C1.74537 4.52232 2.3881 4.03565 3.10594 3.67808ZM7.32421 4.1606C7.05614 4.09431 6.78509 4.25789 6.71881 4.52596C6.65252 4.79403 6.8161 5.06507 7.08417 5.13136C7.84497 5.31948 8.53012 5.70155 9.0839 6.22291C9.28496 6.4122 9.6014 6.40266 9.79069 6.2016C9.97998 6.00054 9.97043 5.6841 9.76938 5.49481C9.09361 4.85861 8.25606 4.39102 7.32421 4.1606ZM4.4567 5.27145C4.71609 5.17675 4.84961 4.8897 4.75491 4.6303C4.66022 4.37091 4.37317 4.23739 4.11377 4.33209C3.4064 4.59032 2.76741 4.98934 2.23051 5.49481C2.02945 5.6841 2.01991 6.00054 2.2092 6.2016C2.39849 6.40266 2.71492 6.4122 2.91598 6.22291C3.35595 5.80869 3.87889 5.48239 4.4567 5.27145ZM5.9999 7C5.38081 7 4.81541 7.22438 4.37866 7.59688C4.16855 7.77607 3.85296 7.75101 3.67377 7.5409C3.49458 7.3308 3.51964 7.01521 3.72974 6.83602C4.34065 6.31499 5.13413 6 5.9999 6C6.27604 6 6.4999 6.22386 6.4999 6.5C6.4999 6.77614 6.27604 7 5.9999 7ZM6 10.5C6.55228 10.5 7 10.0523 7 9.5C7 8.94772 6.55228 8.5 6 8.5C5.44772 8.5 5 8.94772 5 9.5C5 10.0523 5.44772 10.5 6 10.5Z" fill="#5C6B8A"/>
<path d="M1 1L11 11" stroke="#5C6B8A" stroke-linecap="round"/>
</svg>
`;
    this.vehicleHistorySpan = document.createElement('span');
    this.historyLength = assignedCarrierVehicleAreas.value.length;
    this.vehicleHistorySpan.textContent =
      this.historyLength > 0
        ? this.historyLength > 1
          ? `Show Vehicle History`
          : `No Vehicle History`
        : `No Vehicle Location`;
    this.vehicleHistorySpan.className = `text-gray-700 text-xs font-medium leading-none`;
    this.vehicleHistoryButton.innerHTML = this.vehicleHistoryButtonShowSvg;
    this.vehicleHistoryButton.appendChild(this.vehicleHistorySpan);
    this.vehicleHistoryButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const layers = map.getStyle().layers;
      // const clusterLayers = layers.filter(
      //   (layer) =>
      //     layer.id.includes('cluster') && layer.id.includes('history')
      // );
      const historyLayers = layers.filter((layer) =>
        layer.id.includes('vehicle-history')
      );
      if (this.vehicleHistorySpan.textContent === `Show Vehicle History`) {
        if (document.querySelector('.show-history')) {
          document.querySelector('.show-history').remove();
        }
        this.vehicleHistoryButton.innerHTML = this.vehicleHistoryButtonHideSvg;
        this.vehicleHistorySpan.textContent = `Hide Vehicle History`;
        this.vehicleHistoryButton.appendChild(this.vehicleHistorySpan);
        // mapbox.setLayoutProperty(
        //   'layer-assigned-carrier-vehicle-history',
        //   'visibility',
        //   'visible'
        // );
        // clusterLayers.forEach((layer) => {
        //   map.setLayoutProperty(layer.id, 'visibility', 'visible');
        // });
        historyLayers.forEach((layer) => {
          map.setLayoutProperty(layer.id, 'visibility', 'visible');
        });
        const vehicleHistoryCoordinates = [];
        if (assignedCarrierVehicleAreas.value.length) {
          assignedCarrierVehicleAreas.value.forEach((area, index) => {
            if (index !== 0) {
              vehicleHistoryCoordinates.push([area.lon, area.lat]);
            }
          });
        }
        coordinates = {
          ...coordinates,
          'vehicle-history': vehicleHistoryCoordinates,
        };
        delete coordinates['load-stops'];
        extendBounds();
      } else if (
        this.vehicleHistorySpan.textContent === `Hide Vehicle History`
      ) {
        if (document.querySelector('.hide-history')) {
          document.querySelector('.hide-history').remove();
        }
        this.vehicleHistoryButton.innerHTML = this.vehicleHistoryButtonShowSvg;
        this.vehicleHistorySpan.textContent = `Show Vehicle History`;
        this.vehicleHistoryButton.appendChild(this.vehicleHistorySpan);
        // mapbox.setLayoutProperty(
        //   'layer-assigned-carrier-vehicle-history',
        //   'visibility',
        //   'none'
        // );
        // clusterLayers.forEach((layer) => {
        //   map.setLayoutProperty(layer.id, 'visibility', 'none');
        // });
        historyLayers.forEach((layer) => {
          map.setLayoutProperty(layer.id, 'visibility', 'none');
        });
        delete coordinates['vehicle-history'];
        addLoadStopsToBounds();
        extendBounds();
      }
    });
    this.resetResetViewButton = document.createElement('button');
    this.resetResetViewButton.className =
      'reset-view-button invisible pl-1 pr-2 py-1 bg-white rounded-full shadow border justify-start items-center gap-0.5 flex';

    this.resetViewSvg = `<svg height="16px" width="16px" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Layer_1" x="0px" y="0px" viewBox="0 0 512 512" style="width: 16px;height: 16px;enable-background:new 0 0 512 512;" xml:space="preserve">
<g>
	<path d="M221.87,17.07v17.07c51.89,0.01,98.72,20.98,132.74,54.98c34,34.03,54.98,80.85,54.99,132.75   c-0.01,51.89-20.99,98.72-54.99,132.74c-34.03,34-80.85,54.98-132.74,54.99c-51.9-0.01-98.72-20.99-132.75-54.99   c-34-34.03-54.97-80.85-54.98-132.74c0.01-51.9,20.98-98.73,54.98-132.75c34.03-34,80.85-54.97,132.75-54.98V17.07V0   C99.32,0.01,0.01,99.32,0,221.87C0.01,344.4,99.32,443.72,221.87,443.73c122.53-0.01,221.85-99.33,221.87-221.87   C443.72,99.32,344.4,0.01,221.87,0V17.07z"/>
	<path d="M363.4,387.53L482.87,507c6.67,6.67,17.47,6.67,24.14,0s6.67-17.47,0-24.14L387.53,363.4c-6.67-6.67-17.47-6.67-24.14,0   S356.73,380.87,363.4,387.53"/>
	<path d="M153.6,187.73V153.6l34.13,0c9.43,0,17.07-7.64,17.07-17.07c0-9.43-7.64-17.07-17.07-17.07l-51.2,0   c-4.49,0-8.89,1.82-12.07,5c-3.18,3.18-5,7.57-5,12.07v51.2c0,9.43,7.64,17.07,17.07,17.07C145.96,204.8,153.6,197.16,153.6,187.73   L153.6,187.73z M256,153.6h34.13l0,34.13c0,9.43,7.64,17.07,17.07,17.07c9.43,0,17.07-7.64,17.07-17.07l0-51.2   c0-4.49-1.82-8.89-5-12.07c-3.18-3.18-7.57-5-12.07-5H256c-9.43,0-17.07,7.64-17.07,17.07C238.93,145.96,246.57,153.6,256,153.6   L256,153.6z M119.47,256v51.2c0,4.49,1.82,8.89,5,12.07c3.18,3.18,7.57,5,12.07,5h51.2c9.43,0,17.07-7.64,17.07-17.07   c0-9.43-7.64-17.07-17.07-17.07l-34.13,0l0-34.13c0-9.43-7.64-17.07-17.07-17.07C127.11,238.93,119.47,246.57,119.47,256   L119.47,256z M290.13,256v34.13l-34.13,0c-9.43,0-17.07,7.64-17.07,17.07c0,9.43,7.64,17.07,17.07,17.07l51.2,0   c4.49,0,8.89-1.82,12.07-5s5-7.57,5-12.07V256c0-9.43-7.64-17.07-17.07-17.07C297.77,238.93,290.13,246.57,290.13,256L290.13,256z"/>
</g>
<script xmlns=""/></svg>`;
    this.resetViewSpan = document.createElement('span');
    this.resetViewSpan.textContent = `Zoom to Route`;
    this.resetViewSpan.className = `text-gray-700 text-xs font-medium leading-none`;
    this.resetResetViewButton.innerHTML = this.resetViewSvg;
    this.resetResetViewButton.appendChild(this.resetViewSpan);
    this.resetResetViewButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      zoomToLoadRoute();
    });
    if (assignedNearbyVehicles.value.length && canTrackAndTrace.value) {
      this.container.appendChild(this.button);
    }
    if (assignedVehicle.value.carrier_vehicle?.id) {
      this.container.appendChild(this.vehicleHistoryButton);
    }
    this.container.appendChild(this.resetResetViewButton);
    return this.container;
  }

  onRemove() {
    this.container.parentNode?.removeChild(this.container);
    this.map = undefined;
  }
}

const extendBounds = () => {
  const bounds = new mapboxgl.LngLatBounds();
  Object.keys(coordinates).forEach((key) => {
    if (key === 'load-route') {
      return;
    }
    if (key === 'destination-route') {
      bounds.extend(coordinates[key][0]);
      bounds.extend(coordinates[key][coordinates[key].length - 1]);
    }
    coordinates[key]?.forEach((coordinate) => bounds.extend(coordinate));
  });
  if (Object.keys(bounds).length === 0) {
    return;
  }
  mapbox.fitBounds(bounds, {
    zoom: mapbox.getZoom() - 1,
    speed: isMobile.value ? 0.5 : 0.8, // Optional: slower zoom for smoother feel
    curve: 1.5, // Optional: adjust the zoom curve
  });
};
const addCarrierHq = () => {
  const sourceId = 'source-carrier-hq';
  const layerId = 'layer-carrier-hq';
  const layerLabelId = 'layer-carrier-hq-label';

  if (mapbox.getLayer(layerLabelId)) {
    mapbox.removeLayer(layerLabelId);
  }
  if (mapbox.getLayer(layerId)) {
    mapbox.removeLayer(layerId);
  }
  if (mapbox.getSource(sourceId)) {
    mapbox.removeSource(sourceId);
  }
  if (
    load.carrier?.headquarters?.address?.lat &&
    load.carrier?.headquarters?.address?.lon
  ) {
    const point = turf.point(
      [
        load.carrier.headquarters.address.lon,
        load.carrier.headquarters.address.lat,
      ],
      {
        carrier: load.carrier,
        image: 'hq',
        title: 'Carrier HQ',
      }
    );
    mapbox.addSource(sourceId, {
      type: 'geojson',
      data: point,
    });
    mapbox.addLayer({
      id: layerId,
      type: 'symbol',
      source: sourceId,
      layout: {
        'icon-image': ['get', 'image'],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
    mapbox.addLayer({
      id: layerLabelId,
      type: 'symbol',
      source: sourceId,
      layout: {
        'text-field': ['concat', ['get', 'title']],
        'text-size': 12,
        'icon-text-fit': 'both',
        'icon-text-fit-padding': [5, 5, 5, 5],
        'icon-image': 'hq-label',
        'text-justify': 'center',
        'text-variable-anchor': ['left', 'bottom', 'top', 'right'],
        'text-radial-offset': 1.5,
        'text-letter-spacing': 0.02,
        'text-line-height': 1.2,
      },
      paint: {
        'text-color': '#FFFFFF',
      },
    });
  }
};

const addLoadStops = () => {
  // loop over stops and add to collection
  if (mapbox.getLayer('layer-stops-cluster')) {
    mapbox.removeLayer('layer-stops-cluster');
  }
  if (mapbox.getLayer('layer-stops')) {
    mapbox.removeLayer('layer-stops');
  }
  if (mapbox.getLayer('layer-stops-label')) {
    mapbox.removeLayer('layer-stops-label');
  }
  if (mapbox.getSource('source-stops')) {
    mapbox.removeSource('source-stops');
  }
  const features = [];
  if (load.stops?.length > 0) {
    load.stops.forEach((stop, index) => {
      if (stop?.latitude_for_map && stop?.longitude_for_map) {
        let stopTitle;
        if (stop?.country === 'CA') {
          stopTitle = `${stop.code}, Canada`;
        } else {
          stopTitle = `${stop.area_five_zip_city || stop.city}${stop.area_five_zip_state || stop.state ? ',' : ''} ${stop.area_five_zip_state || stop.state || ''}`;
        }

        features.push(
          turf.point([stop.longitude_for_map, stop.latitude_for_map], {
            stop: stop,
            image: index === 0 ? 'origin' : 'destination',
            title: stopTitle,
            'image-label': index === 0 ? 'origin-label' : 'destination-label',
            index: index,
          })
        );
      }
    });
    const collection = turf.featureCollection(features);
    if (!mapbox.getSource('source-stops')) {
      mapbox.addSource('source-stops', {
        type: 'geojson',
        data: collection,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
        clusterProperties: {
          indexes: ['concat', ['concat', ['get', 'index'], ',']],
          titles: ['concat', ['concat', ['get', 'title'], '\n']],
        },
      });
    }
    if (!mapbox.getLayer('layer-stops-cluster')) {
      mapbox.addLayer({
        id: 'layer-stops-cluster',
        type: 'symbol',
        source: 'source-stops',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['concat', ['get', 'titles']],
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [5, 5, 5, 5],
          'icon-image': 'stops-cluster',
          'text-size': 12,
          'text-line-height': 1.2,
          'text-allow-overlap': true,
          'text-ignore-placement': true,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
        paint: {
          'text-color': '#374053',
        },
      });
    }
    // inspect a cluster on click
    mapbox.on('click', 'layer-stops-cluster', (e) => {
      const features = mapbox.queryRenderedFeatures(e.point, {
        layers: [`layer-stops-cluster`],
      });
      const featureClicked = features?.[0];
      if (featureClicked) {
        const clusterId = featureClicked.properties?.cluster_id;
        mapbox
          .getSource('source-stops')
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            mapbox.easeTo({
              center: featureClicked.geometry?.coordinates,
              zoom: zoom,
            });
          });
      }
    });
    // unclustered stops
    if (!mapbox.getLayer('layer-stops')) {
      mapbox.addLayer({
        id: 'layer-stops',
        type: 'symbol',
        source: 'source-stops',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': ['get', 'image'],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
    }
    // stop labels
    if (!mapbox.getLayer('layer-stops-label')) {
      mapbox.addLayer({
        id: 'layer-stops-label',
        type: 'symbol',
        filter: ['!', ['has', 'point_count']],
        source: 'source-stops', // reference the data source
        layout: {
          'text-field': ['concat', ['get', 'title']],
          'text-size': 12,
          'icon-text-fit': 'both',
          'icon-text-fit-padding': [5, 5, 5, 5],
          'icon-image': 'stops-cluster',
          'text-justify': 'center',
          'text-variable-anchor': ['left', 'bottom', 'top', 'right'],
          'text-radial-offset': 1.5,
          'text-letter-spacing': 0.02,
          'text-line-height': 1.2,
        },
        paint: {
          'text-color': '#374053',
        },
      });
    }
  }
};

const addLoadStopsGeofencesLines = () => {
  if (load.stops?.length > 0 && canTrackAndTrace.value) {
    load.stops.forEach((stop, index) => {
      const perimeter = stop?.facility?.perimeter;
      if (
        perimeter &&
        typeof perimeter === 'string' &&
        perimeter.substring(0, 7) === 'POLYGON'
      ) {
        const coordinatesStr = perimeter
          .replace(/^POLYGON \(\(/, '')
          .replace(/\)\)$/, '');
        const coordinatePairs = coordinatesStr.split(', ');
        const coordinates = coordinatePairs.map((pair) => {
          const [lon, lat] = pair.split(' ').map(parseFloat);
          return [lon, lat];
        });
        const sourceId = `source-stop-${index}-line`;
        const layerId = `layer-stop-${index}-line`;
        if (mapbox.getLayer(layerId)) {
          mapbox.removeLayer(layerId);
        }
        if (mapbox.getSource(sourceId)) {
          mapbox.removeSource(sourceId);
        }
        const connectingLineFeature = turf.lineString(coordinates, {
          facility: stop.facility,
          index: index,
        });
        mapbox.addSource(sourceId, {
          type: 'geojson',
          data: connectingLineFeature,
        });
        mapbox.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#F8C612',
            'line-width': 2,
            'line-dasharray': [2, 2],
          },
        });
      }
    });
  }
};

const addLoadStopsGeofences = () => {
  if (mapbox.getLayer('layer-geofences')) {
    mapbox.removeLayer('layer-geofences');
  }
  if (mapbox.getSource('source-geofences')) {
    mapbox.removeSource('source-geofences');
  }
  const features = [];
  if (load.stops?.length > 0 && canTrackAndTrace.value) {
    load.stops.forEach((stop, index) => {
      const perimeter = stop?.facility?.perimeter;
      if (
        perimeter &&
        typeof perimeter === 'string' &&
        perimeter.substring(0, 7) === 'POLYGON'
      ) {
        const coordinatesStr = perimeter
          .replace(/^POLYGON \(\(/, '')
          .replace(/\)\)$/, '');
        const coordinatePairs = coordinatesStr.split(', ');
        const coordinates = coordinatePairs.map((pair) => {
          const [lon, lat] = pair.split(' ').map(parseFloat);
          return [lon, lat]; // Returning as [longitude, latitude]
        });
        features.push(
          turf.polygon([coordinates], {
            facility: stop.facility,
            index: index,
          })
        );
      }
    });
    if (features.length) {
      const collection = turf.featureCollection(features);
      mapbox.addSource('source-geofences', {
        type: 'geojson',
        data: collection,
      });
      mapbox.addLayer(
        {
          id: 'layer-geofences',
          type: 'fill',
          source: 'source-geofences',
          layout: {},
          paint: {
            'fill-color': '#f8c612',
            'fill-opacity': 0.3,
          },
        },
        firstSymbolId
      );
    }
  }
};

const addNoAssignedVehicleRoute = () => {
  const route = loadRoute.value;
  const routeSourceId = `source-load-route-${route?.id}`;
  const routeLayerId = `layer-load-route-${route?.id}`;
  if (mapbox.getLayer(routeLayerId)) {
    mapbox.removeLayer(routeLayerId);
  }
  if (mapbox.getLayer(routeLayerId + '-border')) {
    mapbox.removeLayer(routeLayerId + '-border');
  }
  if (mapbox.getSource(routeSourceId)) {
    mapbox.removeSource(routeSourceId);
  }
  if (!assignedVehicle.value.carrier_vehicle?.id) {
    if (!mapbox.getSource(routeSourceId)) {
      mapbox.addSource(routeSourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: route,
        },
      });
    }
    if (!mapbox.getLayer(routeLayerId)) {
      mapbox.addLayer({
        id: routeLayerId,
        type: 'line',
        source: routeSourceId,
        slot: 'middle',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
          visibility: 'visible',
        },
        paint: {
          'line-color': '#143761',
          'line-width': line_width_outer,
        },
      });
    }
    if (!mapbox.getLayer(routeLayerId + '-border')) {
      mapbox.addLayer({
        id: routeLayerId + '-border',
        type: 'line',
        source: routeSourceId,
        slot: 'middle',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#D5E4F6',
          'line-width': line_width_inner,
        },
      });
    }
  }
};

const addLoadStopsToBounds = () => {
  let stopsCoordinates = [];
  if (load.stops?.length) {
    [...load.stops].map((stop) => {
      if (stop.latitude_for_map && stop.longitude_for_map) {
        stopsCoordinates.push([stop.longitude_for_map, stop.latitude_for_map]);
      }
    });
    coordinates = {
      ...coordinates,
      'load-stops': stopsCoordinates,
    };
  }
};

const addAssignedVehicleLocationToBounds = () => {
  if (assignedCarrierVehicleAreas.value.length) {
    delete coordinates['assigned-vehicle'];
    const vehicleLocationCoordinates = [];
    vehicleLocationCoordinates.push([
      assignedCarrierVehicleAreas.value[0].lon,
      assignedCarrierVehicleAreas.value[0].lat,
    ]);
    coordinates = {
      ...coordinates,
      'vehicle-location': vehicleLocationCoordinates,
    };
  }
};

const addNearbyVehicleLocationsToBounds = () => {
  if (
    !assignedCarrierVehicleAreas.value?.length &&
    nearbyVehicles.value.length
  ) {
    delete coordinates['nearby-vehicles'];
    const nearbyVehicleCoordinates = [];
    nearbyVehicles.value.forEach(({ carrier_vehicle_areas = [] }) => {
      if (carrier_vehicle_areas.length) {
        nearbyVehicleCoordinates.push([
          carrier_vehicle_areas[0].lon,
          carrier_vehicle_areas[0].lat,
        ]);
      }
    });
    coordinates = {
      ...coordinates,
      'nearby-vehicles': nearbyVehicleCoordinates,
    };
  }
};

const addAlertVehicleLocationToBounds = () => {
  const { carrier_vehicle_areas = [] } = alertVehicle.value;
  const alertVehicleCoordinates = [];
  if (
    carrier_vehicle_areas?.length &&
    !assignedNearbyVehicles.value.length &&
    !assignedVehicle.value.carrier_vehicle?.id
  ) {
    alertVehicleCoordinates.push([
      carrier_vehicle_areas[0].lon,
      carrier_vehicle_areas[0].lat,
    ]);
    coordinates = {
      ...coordinates,
      'alert-vehicle': alertVehicleCoordinates,
    };
  }
};

watch(
  () => load,
  async (newValue, oldValue) => {
    if (newValue?.id) {
      if (
        newValue.carrier?.id &&
        newValue.carrier?.id !== oldValue.carrier?.id
      ) {
        if (canTrackAndTrace.value) {
          store.dispatch('loads/getCarrierVehicles', {
            id: newValue.carrier?.id,
          });
        }
      }
    }
  }
);

// helper
const filterArrayWithDictionary = (array, dictionary) =>
  array.filter((item) => !dictionary[item.id]);

const carrierVehiclesList = computed(() =>
  store.state.loads.eld_carrier_vehicles.map((carrierVehicle) => {
    return {
      id: carrierVehicle.id,
      identifier:
        carrierVehicle.truck_number ||
        carrierVehicle.external_id ||
        carrierVehicle.id,
      vin: carrierVehicle.vehicle.vin,
      isUnverified: carrierVehicle.is_uninsured_eld,
      distanceToOrigin: 'unknown',
      isNearby: false,
    };
  })
);

const carrierVehiclesDropdownList = ref([]);

watch(
  () => carrierVehiclesList.value,
  () => {
    setCarrierVehiclesDropdown();
  },
  { deep: true }
);

const hasFetchedRoutes = ref(false);

const shouldAddLayers = computed(() => hasFetchedRoutes.value && hasMap.value);

const nearbyVehiclesListDictionary = computed(() => {
  const ids = {};
  nearbyVehiclesList.value.forEach(({ id }) => {
    ids[id] = true;
  });
  return ids;
});

const setCarrierVehiclesDropdown = () => {
  carrierVehiclesDropdownList.value = [
    ...nearbyVehiclesList.value,
    ...filterArrayWithDictionary(
      carrierVehiclesList.value,
      nearbyVehiclesListDictionary.value
    ),
  ];
  if (assignedVehicle.value.carrier_vehicle?.id) {
    const nearbyAssignedVehicle = carrierVehiclesDropdownList.value.find(
      ({ id }) => id === assignedVehicle.value.carrier_vehicle?.id
    );
    if (nearbyAssignedVehicle) {
      carrierVehiclesDropdownList.value =
        carrierVehiclesDropdownList.value.filter(
          ({ id }) => id !== nearbyAssignedVehicle.id
        );
      carrierVehiclesDropdownList.value.unshift(nearbyAssignedVehicle);
    } else {
      const { carrier_vehicle, vehicle, carrier_vehicle_areas } =
        assignedVehicle.value;
      carrierVehiclesDropdownList.value.unshift({
        id: carrier_vehicle.id,
        identifier:
          carrier_vehicle.truck_number ||
          carrier_vehicle.external_id ||
          carrier_vehicle.id,
        vin: vehicle.vin,
        isUnverified: carrier_vehicle.is_uninsured_eld,
        distanceToOrigin:
          carrier_vehicle_areas[0]?.distance_in_miles || 'unknown',
        isNearby: false,
      });
    }
    carrierVehiclesDropdownList.value.push({
      id: '',
      identifier: 'No Vehicle',
      vin: '',
      distanceToOrigin: 'unknown',
      isNearby: false,
    });
  } else if (carrierVehiclesDropdownList.value.length) {
    carrierVehiclesDropdownList.value.unshift({
      id: '',
      identifier: 'No Vehicle',
      vin: '',
      distanceToOrigin: 'unknown',
      isNearby: false,
    });
  }
  carrierVehiclesDropdownSelected.value = carrierVehiclesDropdownList.value[0];
  showDropdown.value = carrierVehiclesDropdownList.value.length > 1;
};

watch(
  () => shouldAddLayers.value,
  () => {
    if (!shouldAddLayers.value) {
      return;
    }
    addLayers();
    hasFetchedRoutes.value = false;
  }
);

//watch for route changes to reload data

watch(
  () => router.currentRoute.value.query,
  (newValue) => {
    if (!newValue.tab) {
      activePageTab.value = 'map';
      isMapExpanded.value = false;
      return;
    }
    activePageTab.value = newValue.tab;
    isMapExpanded.value = !!newValue.expandMap;
  }
);

onBeforeUnmount(() => {
  mapbox?.remove();
  store.dispatch('loads/clearLoad');
});

const outOfRangeAlert = computed(() =>
  load?.value?.tickets?.find(
    (ticket) => ticket.is_type === 'physical_location_out_of_range_from_origin'
  )
);

const outOfRangeAlertDistance = computed(() => {
  if (outOfRangeAlert.value) {
    // hardcoded to 5th word in the message for now
    return outOfRangeAlert.value?.message.split(' ')?.[5];
  }
  return null;
});
</script>
